// ── SUPABASE CLIENT ───────────────────────────────────────────────────────────
// Anon client only — safe to expose in browser.
// Credentials are loaded from environment variables so each environment
// (local / staging / production) can point to its own Supabase project.
// Admin/service role client lives server-side in api/send-confirmation.js only.
import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  // Fail fast during development so misconfigured env vars are caught immediately.
  console.error(
    '[supabase] REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY is not set.\n' +
    'Copy .env.example → .env.local and fill in the values for your environment.'
  );
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ── AUTH HELPERS ──────────────────────────────────────────────────────────────

/**
 * Sign in with email and password.
 * Returns { data, error } from Supabase.
 */
export async function signIn(email, password) {
  return supabase.auth.signInWithPassword({ email, password });
}

/**
 * Sign up a new user with email and password.
 * meta: { role: 'pt'|'client', name: string }
 * These are stored in user_metadata and used to find the matching profile row.
 */
export async function signUp(email, password, meta) {
  // emailRedirectTo uses the current origin so it works in both
  // local dev (localhost) and production (Vercel URL) automatically.
  return supabase.auth.signUp({
    email,
    password,
    options: {
      data: { role: meta?.role || 'pt', name: meta?.name || '' },
      emailRedirectTo: window.location.origin,
    },
  });
}

/**
 * Sign out the current user.
 */
export async function signOut() {
  return supabase.auth.signOut();
}

/**
 * Send a password reset email.
 */
export async function resetPassword(email) {
  // Prefer explicit env var so the link always returns to the correct environment.
  // Falls back to window.location.origin for local dev where no env var is set.
  const redirectTo = process.env.REACT_APP_SITE_URL || window.location.origin;
  return supabase.auth.resetPasswordForEmail(email, { redirectTo });
}

/**
 * Send a magic login link (OTP) to the email address.
 * User clicks the link and is signed in automatically — no password needed.
 *
 * shouldCreateUser:false is critical — without it, entering an unregistered
 * email here silently creates a half-formed auth account (no role, no pts row)
 * that bypasses the signup + approval flow and dead-ends on the homepage.
 * Account creation must only ever happen through the signup form.
 */
export async function signInWithMagicLink(email) {
  return supabase.auth.signInWithOtp({
    email,
    options: { emailRedirectTo: window.location.origin, shouldCreateUser: false },
  });
}

/**
 * Return the current session (null if not logged in).
 */
export async function getSession() {
  const { data, error } = await supabase.auth.getSession();
  if (error) return null;
  return data.session;
}

/**
 * Return the current user with metadata, or null.
 */
export async function getUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data?.user) return null;
  return data.user;
}
